import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  makeStyles,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";
import ImageIcon from "@material-ui/icons/Image";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityIcon from "@material-ui/icons/Visibility";
import SendIcon from "@material-ui/icons/Send";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Image } from "app/routes/components/routes/Image";
import { useFileManager } from "hooks/useFileManager";
import { useDebounce } from "util/debounceRamda";
import { setPaginationByAmountAndSize } from "util/list-fns";
import { selUser } from "store/slices";
import {
  createSupportTicketComment,
  getSupportTicketById,
  getSupportTicketComments,
  getSupportTickets,
} from "./slice/supportThunk";
import {
  selSupportCurrentTicket,
  selSupportListSize,
  selSupportPage,
  selSupportTicketComments,
  selSupportTicketList,
  selSupportTotalItems,
  setSupportPage,
} from "./slice/supportSlice";

const statusOptions = [
  { value: "all", label: "All" },
  { value: "NEW", label: "New" },
  { value: "WAITING", label: "Waiting" },
  { value: "ANSWERED", label: "Answered" },
  { value: "CLOSED", label: "Closed" },
];

const replyStatusOptions = statusOptions.filter(
  (option) => option.value !== "all"
);

const priorityOptions = [
  { value: "all", label: "All" },
  { value: "LOW", label: "Low" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HIGH", label: "High" },
];

const statusLabels = statusOptions.reduce(
  (acc, item) => ({ ...acc, [item.value]: item.label }),
  {}
);

const priorityLabels = priorityOptions.reduce(
  (acc, item) => ({ ...acc, [item.value]: item.label }),
  {}
);

const statusColors = {
  NEW: "#2196f3",
  WAITING: "#f5a623",
  ANSWERED: "#4caf50",
  CLOSED: "#757575",
};

const buildQuery = (params) =>
  Object.entries(params)
    .filter(
      ([, value]) => value !== "" && value !== null && value !== undefined
    )
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");

const formatDate = (date, format = "MM/DD/YYYY - HH:mm") =>
  date ? moment(date).format(format) : "-";

const normalizeEnum = (value) =>
  value ? String(value).trim().toUpperCase().replace(/\s+/g, "_") : "";

const getPersonName = (createdBy) => {
  const assignment = createdBy?.userAssignment;
  const user = createdBy?.user;
  const fullName = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .join(" ");

  if (assignment?.known_as && assignment?.code) {
    return `${assignment.known_as} (${assignment.code})`;
  }

  return assignment?.known_as || fullName || "-";
};

const getInitials = (createdBy) => {
  const name = getPersonName(createdBy);
  if (!name || name === "-") return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const getUserImageId = (createdBy) => createdBy?.user?.image || null;

const Support = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    FileManager,
    filesData,
    onClearMultipleFile,
    onDownload,
    onMultipleUpload,
    onRemoveFile,
    openFileManager,
  } = useFileManager();
  const currentUser = useSelector(selUser);
  const tickets = useSelector(selSupportTicketList);
  const currentTicket = useSelector(selSupportCurrentTicket);
  const comments = useSelector(selSupportTicketComments);
  const page = useSelector(selSupportPage);
  const size = useSelector(selSupportListSize);
  const totalItems = useSelector(selSupportTotalItems);

  const [detailOpen, setDetailOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [priority, setPriority] = useState("all");
  const [replyStatus, setReplyStatus] = useState("ANSWERED");
  const [reply, setReply] = useState("");
  const [isSendingReply, setIsSendingReply] = useState(false);
  const commentsEndRef = React.useRef(null);
  const [params, setParams] = useState({
    page: 1,
    limit: size,
    sortBy: "updated_time:desc",
  });

  const queryParam = useMemo(() => buildQuery(params), [params]);
  const pagination = {
    cur: page,
    all: setPaginationByAmountAndSize(size, totalItems),
  };
  const conversation = useMemo(() => {
    if (!currentTicket?.id) return [];

    const openingMessage = {
      id: `${currentTicket.id}-opening`,
      description: currentTicket.description,
      attachments: currentTicket.attachments || [],
      created_by: currentTicket.created_by,
      created_time: currentTicket.created_time,
      isOpening: true,
    };

    return [openingMessage, ...comments];
  }, [comments, currentTicket]);

  useEffect(() => {
    dispatch(getSupportTickets(queryParam));
  }, [dispatch, queryParam]);

  useEffect(() => {
    if (!detailOpen) return;
    if (commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [conversation.length, detailOpen]);

  const updateSearchParam = useCallback(
    (value) => {
      dispatch(setSupportPage(1));
      setParams(({ search, ...rest }) =>
        value ? { ...rest, search: value, page: 1 } : { ...rest, page: 1 }
      );
    },
    [dispatch]
  );

  const debouncedSearch = useDebounce(updateSearchParam, 400);

  const handleSearch = useCallback(
    ({ target: { value } }) => {
      setSearch(value);
      debouncedSearch(value.trim());
    },
    [debouncedSearch]
  );

  const handleSelectStatus = ({ target: { value } }) => {
    setStatus(value);
    dispatch(setSupportPage(1));
    setParams(({ status, ...rest }) =>
      value === "all"
        ? { ...rest, page: 1 }
        : { ...rest, status: value, page: 1 }
    );
  };

  const handleSelectPriority = ({ target: { value } }) => {
    setPriority(value);
    dispatch(setSupportPage(1));
    setParams(({ priority, ...rest }) =>
      value === "all"
        ? { ...rest, page: 1 }
        : { ...rest, priority: value, page: 1 }
    );
  };

  const handleChangePage = (event, nextPage) => {
    dispatch(setSupportPage(nextPage));
    setParams((prev) => ({ ...prev, page: nextPage }));
  };

  const handleOpenDetail = (ticket) => {
    setDetailOpen(true);
    setReply("");
    setReplyStatus(normalizeEnum(ticket.status) || "ANSWERED");
    dispatch(getSupportTicketById(ticket.id));
    dispatch(getSupportTicketComments(ticket.id));
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setReply("");
    onClearMultipleFile();
  };

  const handleSubmitReply = async () => {
    if (!currentTicket?.id || isSendingReply) return;

    setIsSendingReply(true);
    try {
      let attachments = [];
      if (filesData.length) {
        // Private, but visible to the ticket's own airline staff too (not just Enroute superadmins).
        const uploadRes = await onMultipleUpload(false, true);
        attachments = (uploadRes?.message || []).map((item) => item.id);
      }

      dispatch(
        createSupportTicketComment(
          currentTicket.id,
          {
            description: reply,
            status: replyStatus,
            attachments,
          },
          queryParam,
          (error) => {
            if (error) {
              setIsSendingReply(false);
              return;
            }
            setReply("");
            onClearMultipleFile();
            setIsSendingReply(false);
          }
        )
      );
    } catch (error) {
      setIsSendingReply(false);
    }
  };

  const renderAttachments = (attachments = []) => {
    if (!attachments.length) return null;

    return (
      <div className={classes.attachments}>
        {attachments.map((attachment) => {
          const fileId = attachment?.id || attachment;
          return (
            <div className={classes.attachment} key={fileId}>
              <Image
                onClick={() => onDownload(fileId)}
                imageId={fileId}
                mimeTypeFile={attachment?.mimetype || "image/jpeg"}
                objectFit="cover"
              />
              <Typography className={classes.attachmentName}>
                {attachment?.originalname || fileId}
              </Typography>
            </div>
          );
        })}
      </div>
    );
  };

  const renderConversationMessage = (message) => {
    const ownerId =
      currentTicket?.created_by?.user?.id ||
      currentTicket?.created_by?.user?._id;
    const authorId =
      message?.created_by?.user?.id || message?.created_by?.user?._id;
    const currentUserId = currentUser?.id || currentUser?._id;
    const isCurrentUser = Boolean(authorId && authorId === currentUserId);
    const isSupportSide =
      isCurrentUser || Boolean(ownerId && authorId !== ownerId);
    const author = getPersonName(message.created_by);

    return (
      <div
        className={`${classes.messageRow} ${
          isSupportSide ? classes.messageRowReverse : ""
        }`}
        key={message.id || message.created_time}
      >
        <Avatar className={classes.avatar}>
          {getUserImageId(message.created_by) ? (
            <Image
              imageId={getUserImageId(message.created_by)}
              mimeTypeFile="image/jpeg"
              objectFit="cover"
              style={{ borderRadius: "50%", overflow: "hidden" }}
            />
          ) : (
            getInitials(message.created_by)
          )}
        </Avatar>
        <Paper
          elevation={1}
          className={`${classes.messageBubble} ${
            isSupportSide ? classes.messageBubbleReverse : ""
          }`}
        >
          <div className={classes.messageHeader}>
            <Typography className={classes.messageAuthor}>{author}</Typography>
            {message.isOpening && (
              <Typography className={classes.openingLabel}>
                Ticket opened
              </Typography>
            )}
          </div>
          <div
            className={classes.messageBody}
            dangerouslySetInnerHTML={{
              __html: message.description || "-",
            }}
          />
          {renderAttachments(message.attachments)}
          <Typography className={classes.messageTime}>
            {formatDate(message.created_time)}
          </Typography>
        </Paper>
      </div>
    );
  };

  const canSubmitReply = Boolean(
    currentTicket?.id &&
      !isSendingReply &&
      (reply.trim() ||
        filesData.length ||
        replyStatus !== normalizeEnum(currentTicket?.status))
  );

  return (
    <Box>
      <div className="page-heading d-sm-flex flex-row justify-content-sm-between">
        <h2 className="title">Support</h2>
      </div>

      <Paper className={classes.filterBar}>
        <TextField
          placeholder="Search tickets..."
          value={search}
          onChange={handleSearch}
          className={classes.searchInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="disabled" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl variant="outlined" className={classes.filterControl}>
          <InputLabel>Status</InputLabel>
          <Select value={status} onChange={handleSelectStatus} label="Status">
            {statusOptions.map((option) => (
              <MenuItem value={option.value} key={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" className={classes.filterControl}>
          <InputLabel>Priority</InputLabel>
          <Select
            value={priority}
            onChange={handleSelectPriority}
            label="Priority"
          >
            {priorityOptions.map((option) => (
              <MenuItem value={option.value} key={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <Paper elevation={5}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">Ticket No</TableCell>
                <TableCell align="center">Subject</TableCell>
                <TableCell align="center">Airline</TableCell>
                <TableCell align="center">Created By</TableCell>
                <TableCell align="center">Last Modified</TableCell>
                <TableCell align="center">Priority</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((ticket) => {
                const normalizedStatus = normalizeEnum(ticket.status);
                const normalizedPriority = normalizeEnum(ticket.priority);

                return (
                  <TableRow
                    key={ticket.id}
                    hover
                    className={classes.tableRow}
                    onClick={() => handleOpenDetail(ticket)}
                  >
                    <TableCell align="center">
                      {ticket.ticket_number || "-"}
                    </TableCell>
                    <TableCell align="center">{ticket.title || "-"}</TableCell>
                    <TableCell align="center">
                      {ticket.airline?.name || "-"}
                    </TableCell>
                    <TableCell align="center">
                      {getPersonName(ticket.created_by)}
                    </TableCell>
                    <TableCell align="center">
                      {formatDate(ticket.updated_time)}
                    </TableCell>
                    <TableCell align="center">
                      {priorityLabels[normalizedPriority] ||
                        ticket.priority ||
                        "-"}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={
                          statusLabels[normalizedStatus] || ticket.status || "-"
                        }
                        className={classes.statusChip}
                        style={{
                          backgroundColor:
                            statusColors[normalizedStatus] || "#9e9e9e",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View ticket">
                        <IconButton
                          color="primary"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenDetail(ticket);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!tickets.length && (
                <TableRow>
                  <TableCell align="center" colSpan={8}>
                    No support tickets found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          count={pagination.all}
          page={pagination.cur}
          shape="rounded"
          color="primary"
          onChange={handleChangePage}
          className={classes.pagination}
        />
      </Paper>

      <Modal open={detailOpen} onClose={handleCloseDetail}>
        <Box className={classes.detailModal}>
          <div className={classes.detailHeader}>
            <div>
              <Typography className={classes.ticketNumber}>
                {currentTicket?.ticket_number || "Ticket"}
              </Typography>
              <Typography className={classes.ticketTitle}>
                {currentTicket?.title || "-"}
              </Typography>
            </div>
            <div className={classes.headerActions}>
              <Chip
                size="small"
                label={
                  statusLabels[normalizeEnum(currentTicket?.status)] ||
                  currentTicket?.status ||
                  "-"
                }
                className={classes.statusChip}
                style={{
                  backgroundColor:
                    statusColors[normalizeEnum(currentTicket?.status)] ||
                    "#9e9e9e",
                }}
              />
              <IconButton size="small" onClick={handleCloseDetail}>
                <CloseIcon />
              </IconButton>
            </div>
          </div>

          <div className={classes.detailGrid}>
            <Typography>
              <strong>Airline:&nbsp;</strong>
              {currentTicket?.airline?.name || "-"}
            </Typography>
            <Typography>
              <strong>Created By:&nbsp;</strong>
              {getPersonName(currentTicket?.created_by)}
            </Typography>
            <Typography>
              <strong>Priority:&nbsp;</strong>
              {priorityLabels[normalizeEnum(currentTicket?.priority)] ||
                currentTicket?.priority ||
                "-"}
            </Typography>
            <Typography>
              <strong>Created Time:&nbsp;</strong>
              {formatDate(currentTicket?.created_time)}
            </Typography>
            <Typography>
              <strong>Updated Time:&nbsp;</strong>
              {formatDate(currentTicket?.updated_time)}
            </Typography>
          </div>

          <Divider />

          <div className={classes.conversationHeader}>
            <Typography className={classes.blockTitle}>Conversation</Typography>
            <Typography color="textSecondary">
              {conversation.length} message(s)
            </Typography>
          </div>
          <div className={classes.commentsPane}>
            {conversation.map(renderConversationMessage)}
            <div ref={commentsEndRef} />
          </div>

          <Divider className={classes.divider} />
          {!!filesData.length && (
            <div className={classes.selectedFiles}>
              {filesData.map(({ file, url }) => {
                const isImage = file?.type?.includes("image/");
                return (
                  <Paper
                    variant="outlined"
                    className={classes.selectedFile}
                    key={url}
                  >
                    <div className={classes.selectedFilePreview}>
                      {isImage ? (
                        <img src={url} alt={file.name} />
                      ) : (
                        <InsertDriveFileIcon />
                      )}
                    </div>
                    <div className={classes.selectedFileInfo}>
                      <Typography className={classes.selectedFileName}>
                        {file.name}
                      </Typography>
                      <Typography color="textSecondary">
                        {(file.size / (1024 * 1024)).toFixed(2)} Mb
                      </Typography>
                    </div>
                    <IconButton
                      size="small"
                      onClick={() => onRemoveFile(url)}
                      disabled={isSendingReply}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                );
              })}
            </div>
          )}
          <div className={classes.replyBar}>
            <FileManager multiple>{null}</FileManager>
            <FormControl variant="outlined" className={classes.replyStatus}>
              <InputLabel>Status</InputLabel>
              <Select
                value={replyStatus}
                onChange={({ target }) => setReplyStatus(target.value)}
                label="Status"
              >
                {replyStatusOptions.map((option) => (
                  <MenuItem value={option.value} key={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              variant="outlined"
              label="Reply"
              value={reply}
              onChange={({ target }) => setReply(target.value)}
              multiline
              rows={3}
              className={classes.replyInput}
            />
            <div className={classes.attachActions}>
              <Tooltip title="Attach image">
                <IconButton
                  onClick={() => openFileManager("image")}
                  disabled={isSendingReply}
                >
                  <ImageIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Attach PDF">
                <IconButton
                  onClick={() => openFileManager("pdf")}
                  disabled={isSendingReply}
                >
                  <AttachFileIcon />
                </IconButton>
              </Tooltip>
            </div>
            <Button
              color="primary"
              variant="contained"
              disabled={!canSubmitReply}
              onClick={handleSubmitReply}
              className={classes.sendButton}
              startIcon={<SendIcon />}
            >
              {isSendingReply ? "Sending..." : "Send"}
            </Button>
          </div>
        </Box>
      </Modal>
    </Box>
  );
};

export default Support;

const useStyles = makeStyles((theme) => ({
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  searchInput: {
    minWidth: 280,
    flex: 1,
  },
  filterControl: {
    minWidth: 160,
  },
  tableRow: {
    cursor: "pointer",
  },
  statusChip: {
    color: "#fff",
    minWidth: 82,
    textTransform: "capitalize",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  detailModal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "min(960px, calc(100vw - 32px))",
    height: "min(780px, calc(100vh - 48px))",
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    boxShadow: theme.shadows[5],
    outline: "none",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  detailHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3),
    backgroundColor: "#f8f9fc",
    borderBottom: "1px solid #e3e7ef",
  },
  ticketNumber: {
    fontSize: 16,
    fontWeight: 700,
  },
  ticketTitle: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    overflowWrap: "anywhere",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(120px, 1fr))",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "repeat(2, minmax(140px, 1fr))",
    },
  },
  blockTitle: {
    fontWeight: 600,
    margin: 0,
  },
  attachments: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  attachment: {
    cursor: "pointer",
    width: 110,
  },
  attachmentName: {
    fontSize: 12,
    marginTop: theme.spacing(0.5),
    overflowWrap: "anywhere",
  },
  conversationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1.5, 3),
  },
  commentsPane: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3, 3),
    overflowY: "auto",
    backgroundColor: "#f3f5f9",
  },
  messageRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(1),
  },
  messageRowReverse: {
    flexDirection: "row-reverse",
  },
  avatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    fontSize: 13,
    backgroundColor: "#9aa5b8",
  },
  messageBubble: {
    maxWidth: "min(620px, 78%)",
    width: "fit-content",
    padding: theme.spacing(1.25, 1.5),
    borderRadius: "15px 15px 15px 0",
    backgroundColor: theme.palette.common.white,
    overflowWrap: "anywhere",
  },
  messageBubbleReverse: {
    borderRadius: "15px 15px 0 15px",
    backgroundColor: "#e8f1ff",
  },
  messageHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
  },
  messageAuthor: {
    fontWeight: 600,
    fontSize: 13,
  },
  openingLabel: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    backgroundColor: "#eef1f6",
    borderRadius: 10,
    padding: theme.spacing(0.25, 1),
  },
  messageBody: {
    fontSize: 14,
    lineHeight: 1.6,
    overflowWrap: "anywhere",
    "& p": {
      margin: 0,
    },
  },
  messageTime: {
    color: theme.palette.text.secondary,
    fontSize: 11,
    marginTop: theme.spacing(1),
    textAlign: "right",
  },
  divider: {
    margin: 0,
  },
  replyBar: {
    display: "grid",
    gridTemplateColumns: "160px 1fr auto auto",
    gap: theme.spacing(2),
    alignItems: "flex-start",
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.background.paper,
    borderTop: "1px solid #e3e7ef",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  replyStatus: {
    minWidth: 160,
  },
  replyInput: {
    minWidth: 260,
  },
  attachActions: {
    display: "flex",
    alignItems: "center",
    minHeight: 56,
  },
  sendButton: {
    minHeight: 56,
  },
  selectedFiles: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 3),
    backgroundColor: theme.palette.background.paper,
    borderTop: "1px solid #e3e7ef",
  },
  selectedFile: {
    display: "grid",
    gridTemplateColumns: "48px 1fr auto",
    gap: theme.spacing(1),
    alignItems: "center",
    padding: theme.spacing(1),
    minWidth: 0,
  },
  selectedFilePreview: {
    width: 48,
    height: 48,
    borderRadius: 4,
    backgroundColor: "#f3f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
  },
  selectedFileInfo: {
    minWidth: 0,
  },
  selectedFileName: {
    fontSize: 13,
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));
